/**
 * Composable to persist and restore filters per category slug
 */
export const usePersistedFilters = (categorySlug: string) => {
  const STORAGE_KEY_PREFIX = 'vodi4ka-filters-'
  /** v2 stores slider ranges as gross (incl. VAT) prices, matching the shop UI. */
  const STORAGE_VERSION = 2

  /**
   * Save filters to sessionStorage
   */
  const saveFilters = (
    valueFilters: Record<string, string | string[]>,
    sliderFilters: Record<string, number[]>
  ) => {
    if (typeof window === 'undefined') return

    try {
      const key = `${STORAGE_KEY_PREFIX}${categorySlug}`
      const data = {
        version: STORAGE_VERSION,
        valueFilters,
        sliderFilters,
        timestamp: Date.now()
      }
      sessionStorage.setItem(key, JSON.stringify(data))
    } catch (e) {
      console.warn('Failed to save filters to sessionStorage:', e)
    }
  }

  /**
   * Load filters from sessionStorage
   */
  const loadFilters = (): {
    valueFilters: Record<string, string | string[]>
    sliderFilters: Record<string, number[]>
  } | null => {
    if (typeof window === 'undefined') return null

    try {
      const key = `${STORAGE_KEY_PREFIX}${categorySlug}`
      const stored = sessionStorage.getItem(key)

      if (!stored) return null

      const data = JSON.parse(stored)

      // Optional: Clear old data (older than 24 hours)
      const MAX_AGE = 24 * 60 * 60 * 1000 // 24 hours
      if (data.timestamp && Date.now() - data.timestamp > MAX_AGE) {
        sessionStorage.removeItem(key)
        return null
      }

      // Drop pre-v2 payloads (net slider values) to avoid wrong gross ranges.
      if (data.version !== STORAGE_VERSION) {
        sessionStorage.removeItem(key)
        return null
      }

      return {
        valueFilters: data.valueFilters || {},
        sliderFilters: data.sliderFilters || {}
      }
    } catch (e) {
      console.warn('Failed to load filters from sessionStorage:', e)
      return null
    }
  }

  /**
   * Clear persisted filters for this category
   */
  const clearPersistedFilters = () => {
    if (typeof window === 'undefined') return

    try {
      const key = `${STORAGE_KEY_PREFIX}${categorySlug}`
      sessionStorage.removeItem(key)
    } catch (e) {
      console.warn('Failed to clear filters from sessionStorage:', e)
    }
  }

  /**
   * Check if there are persisted filters
   */
  const hasPersistedFilters = (): boolean => {
    if (typeof window === 'undefined') return false

    try {
      const key = `${STORAGE_KEY_PREFIX}${categorySlug}`
      return sessionStorage.getItem(key) !== null
    } catch (e) {
      return false
    }
  }

  return {
    saveFilters,
    loadFilters,
    clearPersistedFilters,
    hasPersistedFilters
  }
}
